'use client'

import { type DetailedHTMLProps, type InputHTMLAttributes, useContext, useState } from 'react'

import cn from 'clsx'
import { ThemeContext } from '@/utils/themeContext'

import { VisuallyHidden } from '@/components/VisuallyHidden/VisuallyHidden'

import css from './toggle.module.css'

export interface ToggleProps
    extends Omit<
        DetailedHTMLProps<InputHTMLAttributes<HTMLInputElement>, HTMLInputElement>,
        'id' | 'size' | 'onChange'
    > {
    id: string
    size?: '24' | '36'
    onChange: (value: boolean) => void
}

export function Toggle({
    id,
    checked = false,
    size = '36',
    disabled,
    onChange,
    className,
    ...rest
}: ToggleProps) {
    const { theme = 'todoist' } = useContext(ThemeContext)
    const [isChecked, setIsChecked] = useState(checked)

    function onChangeHandler() {
        setIsChecked((value) => !value)

        if (onChange) {
            onChange(!isChecked)
        }
    }

    return (
        <label htmlFor={id} className={cn(css.toggleContainer, className, css[theme])}>
            <VisuallyHidden
                tag="input"
                id={id}
                type="checkbox"
                className={css.toggleCheckbox}
                checked={isChecked}
                disabled={disabled}
                onChange={onChangeHandler}
                {...rest}
            />
            <span
                className={cn(css.visualToggle, {
                    [css.size24]: size === '24',
                })}
                aria-hidden
            />
        </label>
    )
}
